import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Table, Thead, Trow, Tcol, Tbody } from "../../Generic/Table";
import SearchBar from "../../Generic/SearchBar";
import Loader from "../../Generic/Loader";
import EmptyState from "../../Generic/EmptyState";
import dummy from "../../Assets/dummyDish.png";
import { errorNotification } from "../../Generic/Notifications";
import StudioService from "../Services/StudioService";
import { useDish } from "./context/DishContext";
import { AppRoutes, DashboardModules, StudioSubModules } from "../../config/app.config";

const DISH_PAGE_LIMIT = 20;
const DISH_SEARCH_MIN_KEYWORD_LENGTH = 3;
const DISH_SEARCH_DEBOUNCE_MS = 400;

const Page = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 1.5rem;
	gap: 1.5rem;
`;

const StyledEmptyState = styled(EmptyState)`
	margin: 0;
`;

const DishListToolbar = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1.5rem;
`;

const DishListTitle = styled.div`
	font-size: 2.3rem;
	font-weight: 600;
	color: #000;
	width: 100%;
`;

const DishListTableWrap = styled.div`
	overflow-x: hidden;
	overflow-y: auto;
	width: 100%;
	height: max-content;
`;

const DishListTableImage = styled.img`
	height: 100px;
	object-fit: contain;
`;

function getDishImageSrc(dish) {
	if (!dish?.imageBaseUrl || !dish?.imagePaths?.[0]) {
		return dummy;
	}
	return `${dish.imageBaseUrl.replace(/\/$/, "")}/${dish.imagePaths[0]}`;
}

function formatDate(date) {
	if(!date) return "-";
	return new Date(date).toLocaleString();
}

function scrollDishRowIntoView(dishId) {
	const element = typeof document !== "undefined" ? document.getElementById(dishId) : null;
	if (!element) {
		return;
	}
	element.scrollIntoView({ behavior: "instant", block: "nearest", inline: "nearest" });
}

const DishList = () => {
	const navigate = useNavigate();
	const {
		dishSearchQuery,
		setDishSearchQuery,
		paginatedDishes,
		setPaginatedDishes,
		searchedDishes,
		setSearchedDishes,
		paginationPage,
		setPaginationPage,
		hasMorePaginatedPages,
		setHasMorePaginatedPages,
		hasDishListError,
		setHasDishListError,
		listCacheStale,
		markDishListCacheFreshAfterFetch,
		markPaginatedPageLoaded,
		isPaginatedPageLoaded,
		clearLoadedPaginatedPages,
		dishDetailsListPrefetch,
		setDishDetailsListPrefetch,
	} = useDish();

	const dishSearchQueryRef = useRef(dishSearchQuery);
	dishSearchQueryRef.current = dishSearchQuery;

	// Monotonic id per in-flight search; later responses must not overwrite state from a newer query or after leaving search.
	const dishSearchRequestSeqRef = useRef(0);

	const [isPaginatedListLoading, setIsPaginatedListLoading] = useState(false);
	const [isSearchingDishes, setIsSearchingDishes] = useState(false);
	const [infiniteScrollSentinelElement, setInfiniteScrollSentinelElement] = useState();
	const isPaginatedListLoadingRef = useRef(false);
	isPaginatedListLoadingRef.current = isPaginatedListLoading;

	const trimmedSearch = dishSearchQuery.trim();
	const isDishSearchMode = trimmedSearch.length >= DISH_SEARCH_MIN_KEYWORD_LENGTH;

	const runPaginatedFetch = useCallback(
		async (page) => {
			if (dishSearchQueryRef.current.trim().length >= DISH_SEARCH_MIN_KEYWORD_LENGTH) {
				return;
			}
			if (!listCacheStale && isPaginatedPageLoaded(page)) {
				return;
			}
			setIsPaginatedListLoading(true);
			const response = await StudioService.getDishesPaginated({ page, limit: DISH_PAGE_LIMIT });
			const { data, error } = response;
			if (error) {
				setIsPaginatedListLoading(false);
				setHasDishListError(true);
				errorNotification("Could not load dishes");
				return;
			}
			const { dishes, totalCount } = data;
			setPaginatedDishes((previousDishes) => {
				const nextDishes = page === 1 ? dishes : [...previousDishes, ...dishes];
				let hasMore = true;
				if (totalCount > 0 && nextDishes.length >= totalCount) {
					hasMore = false;
				}
				setHasMorePaginatedPages(hasMore);
				return nextDishes;
			});
			markPaginatedPageLoaded(page);
			if (listCacheStale) {
				markDishListCacheFreshAfterFetch();
			}
			setIsPaginatedListLoading(false);
			setHasDishListError(false);
		},
		[
			listCacheStale,
			isPaginatedPageLoaded,
			markPaginatedPageLoaded,
			markDishListCacheFreshAfterFetch,
			setPaginatedDishes,
			setHasDishListError,
			setHasMorePaginatedPages,
		]
	);

	useEffect(() => {
		runPaginatedFetch(paginationPage);
	}, [paginationPage, runPaginatedFetch]);

	useEffect(() => {
		if (!isDishSearchMode) {
			// Invalidate any search request still awaiting a response so its completion is ignored.
			dishSearchRequestSeqRef.current += 1;
			setSearchedDishes([]);
			setIsSearchingDishes(false);
			return;
		}

		setSearchedDishes([]);
		setIsSearchingDishes(true);

		const querySnapshot = trimmedSearch;
		const debounceTimerId = setTimeout(async () => {
			if (dishSearchQueryRef.current.trim() !== querySnapshot) {
				return;
			}
			// Stamp this request so an older slow response cannot apply after a newer search or exit-search bump.
			const requestSeq = ++dishSearchRequestSeqRef.current;
			const response = await StudioService.searchDishesByTitle(querySnapshot);
			if (requestSeq !== dishSearchRequestSeqRef.current) {
				return;
			}
			if (dishSearchQueryRef.current?.trim() !== querySnapshot) {
				return;
			}
			const { data, error } = response;
			if (error) {
				setSearchedDishes([]);
				setIsSearchingDishes(false);
				errorNotification("Dish search failed");
				return;
			}
			setSearchedDishes(Array.isArray(data?.dishes) ? data.dishes : []);
			setIsSearchingDishes(false);
		}, DISH_SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(debounceTimerId);
	}, [dishSearchQuery, isDishSearchMode, trimmedSearch, setSearchedDishes]);

	const loadNextPaginationPage = useCallback(() => {
		if (!hasMorePaginatedPages) {
			return;
		}
		if (isPaginatedListLoadingRef.current) {
			return;
		}
		setPaginationPage((current) => current + 1);
	}, [hasMorePaginatedPages, setPaginationPage]);

	const intersectionObserver = useRef(
		new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					loadNextPaginationPage();
				}
			},
			{ threshold: 0.5 }
		)
	);

	useEffect(() => {
		if (isDishSearchMode) {
			return;
		}
		const node = infiniteScrollSentinelElement;
		const observerInstance = intersectionObserver.current;
		if (node) {
			observerInstance.observe(node);
		}
		return () => {
			if (node) {
				observerInstance.unobserve(node);
			}
		};
	}, [infiniteScrollSentinelElement, isDishSearchMode, loadNextPaginationPage, hasMorePaginatedPages]);


	const didScrollToPrefetchDishRowRef = useRef(false);
	const prevPrefetchDishIdForScrollRef = useRef(dishDetailsListPrefetch?.dishId ?? null);

	useEffect(() => {
		const dishId = dishDetailsListPrefetch?.dishId ?? null;
		if (prevPrefetchDishIdForScrollRef.current !== dishId) {
			didScrollToPrefetchDishRowRef.current = false;
			prevPrefetchDishIdForScrollRef.current = dishId;
		}
		if (hasDishListError || !dishId) {
			return;
		}
		if (didScrollToPrefetchDishRowRef.current) {
			return;
		}
		const raf = requestAnimationFrame(() => {
			scrollDishRowIntoView(dishId);
		});
		didScrollToPrefetchDishRowRef.current = true;
		return () => cancelAnimationFrame(raf);
	}, [hasDishListError, dishDetailsListPrefetch?.dishId]);

	const showSearchLoader = isDishSearchMode && isSearchingDishes;
	const showTableLoader = !isDishSearchMode && isPaginatedListLoading;
	const dishesToDisplay = isDishSearchMode ? searchedDishes : paginatedDishes;

	const dishDetailsPathForId = (dishId) =>
		AppRoutes[DashboardModules.STUDIO][StudioSubModules.DISH].DISH_DETAILS.getPath(dishId);

	function handleDishRowNavigate(dish) {
		setDishDetailsListPrefetch({ dishId: dish._id, title: dish.title ?? "" });
		navigate(dishDetailsPathForId(dish._id));
	}

	return (
		<Page>
			<DishListToolbar>
				<DishListTitle>Dishes</DishListTitle>
				<SearchBar
					width="30rem"
					placeholder={`Search dishes by title (min ${DISH_SEARCH_MIN_KEYWORD_LENGTH} characters)`}
					value={dishSearchQuery}
					onChange={(event) => setDishSearchQuery(event.target.value)}
				/>
			</DishListToolbar>

			{hasDishListError && !showTableLoader && (
				<StyledEmptyState
					retry={() => {
						setHasDishListError(false);
						setHasMorePaginatedPages(true);
						clearLoadedPaginatedPages();
						setPaginatedDishes([]);
						if (paginationPage === 1) {
							runPaginatedFetch(1);
						} else {
							setPaginationPage(1);
						}
					}}
				/>
			)}

			{!hasDishListError && (
				<>
					<DishListTableWrap>
						<Table>
							<Trow head top="0">
								<Thead></Thead>
								<Thead>Name</Thead>
								<Thead>Created At</Thead>
								<Thead>Last Updated</Thead>
							</Trow>
							<Tbody>
								{dishesToDisplay.map((dish, index, array) => (
									<Trow id={dish._id} clickable key={dish._id} innerRef={(node) => index === array.length - 1 ? setInfiniteScrollSentinelElement(node) : null} onClick={() => handleDishRowNavigate(dish)}>
										<Tcol title="" width="10%" borderRadius="10px 0 0 10px">
											<DishListTableImage
												loading="lazy"
												src={getDishImageSrc(dish)}
												alt={dish.title}
												onError={(event) => {
													event.currentTarget.src = dummy;
												}}
											/>
										</Tcol>
										<Tcol title="Name">
											{dish.title}
										</Tcol>
										<Tcol title="Created At">{formatDate(dish.createdAt)}</Tcol>
										<Tcol borderRadius="0 10px 10px 0" title="Last Updated">{formatDate(dish.updatedAt)}</Tcol>
									</Trow>
								))}
							</Tbody>
						</Table>
					</DishListTableWrap>
				</>
			)}

			{showSearchLoader && <Loader />}
			{showTableLoader && <Loader />}
		</Page>
	);
};

export default DishList;
